import { ArrowSquareOut, Lock } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { CustomProxyForm } from "common/components/Proxy";
import { Button } from "common/components/ui/Button";
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "common/components/ui/Dialog";
import {
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from "common/components/ui/Drawer";
import useProxies from "common/datahooks/useProxies";
import { trackMetaPixel } from "common/helpers/utils";
import useExtensionLogin from "common/hooks/useExtensionLogin";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { WorkspaceAccount } from "common/types";

import ExtensionLogin from "./ExtensionLogin";
import LoginMethodSelection from "./LoginMethodSelection";
import PasswordLogin from "./PasswordLogin";

export interface LinkedInLoginProps {
  onSuccess: () => void;
  account?: WorkspaceAccount;
}

export default function LinkedInLogin({
  onSuccess,
  account = null,
}: LinkedInLoginProps) {
  const isDesktop = useTwBreakpoint("lg");

  // This is used to prevent the login method from being set to the default value
  const hasUserSelectedMethodRef = useRef(false);

  const { suggestedCountryCode } = useProxies();
  const {
    extensionState,
    isExtensionLoginVisible,
    extensionName,
    extensionUrl,
  } = useExtensionLogin();

  const [selectedProxy, setSelectedProxy] = useState<string>();
  const [currentAction, setCurrentAction] = useState<
    "selectMethod" | "login" | "addProxy"
  >("selectMethod");
  const [selectedLoginMethod, setSelectedLoginMethod] = useState<
    "email" | "extension"
  >("email");

  // Set the proxy location to the suggested country if it's not set
  useEffect(() => {
    if (!selectedProxy && suggestedCountryCode) {
      setSelectedProxy(suggestedCountryCode);
    }
  }, [suggestedCountryCode]);

  useEffect(() => {
    //if extension option is not visible, we go straight to proxy selection
    if (!isExtensionLoginVisible) {
      setCurrentAction("login");
      // if the extension is available and the user hasn't selected a method yet, we select extension as login method
    } else if (
      extensionState === "available" &&
      !hasUserSelectedMethodRef.current
    ) {
      setSelectedLoginMethod("extension");
    }
  }, [extensionState, isExtensionLoginVisible]);
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  if (currentAction === "selectMethod") {
    return (
      <>
        <Header
          title={
            <>{account ? "Re-connect" : "Connect"} your LinkedIn account</>
          }
        />

        <Body className="gap-y-4">
          <LoginMethodSelection
            selectedLoginMethod={selectedLoginMethod}
            setSelectedLoginMethod={(type) => {
              hasUserSelectedMethodRef.current = true;
              setSelectedLoginMethod(type);
            }}
          />

          <div className="flex items-center justify-center gap-x-0.5">
            <Lock weight="fill" className="size-4 text-black-700" />

            <span className="text-xs text-black-700">
              Secured with TLS 1.3 encryption
            </span>
          </div>
        </Body>

        <Footer className="justify-end">
          {extensionState === "unavailable" &&
          selectedLoginMethod === "extension" ? (
            <Button rightIcon={<ArrowSquareOut />} asChild>
              <Link
                to={extensionUrl}
                target="_blank"
                onAuxClick={() => {
                  trackMetaPixel("trackCustom", "ChromeExtensionInstall");
                }}
                onClick={() => {
                  trackMetaPixel("trackCustom", "ChromeExtensionInstall");
                }}
              >
                {`Get ${extensionName} extension`}
              </Link>
            </Button>
          ) : (
            <Button
              onClick={() => {
                trackMetaPixel(
                  "trackCustom",
                  selectedLoginMethod === "email"
                    ? "ManualConnection"
                    : "ExtensionLogin",
                );
                setCurrentAction("login");
              }}
            >
              Next
            </Button>
          )}
        </Footer>
      </>
    );
  }

  if (currentAction === "login") {
    return selectedLoginMethod === "email" ? (
      <PasswordLogin
        account={account}
        onSuccess={onSuccess}
        setCurrentAction={setCurrentAction}
        selectedProxy={selectedProxy}
        setSelectedProxy={setSelectedProxy}
      />
    ) : (
      <ExtensionLogin
        selectedProxy={selectedProxy}
        setSelectedProxy={setSelectedProxy}
        account={account}
        setCurrentAction={setCurrentAction}
        onSuccess={onSuccess}
      />
    );
  }

  if (currentAction === "addProxy") {
    return (
      <CustomProxyForm
        onBack={() => setCurrentAction("login")}
        onAdded={setSelectedProxy}
      />
    );
  }
}
